import { queriesRequirementDefinitions } from "@query";
import {
  toWSDateString,
  WSMarginProps,
  WSTable,
  WSTableCell,
  WSTableColumnSortDirection
} from "@wingspanhq/fe-component-library";
import { Engagement, EngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { useHistory } from "react-router-dom";
import { ContractorsCountCell } from "./ContractorsCountCell";
import { useEngagementTypeDefaultActions } from "../../hooks/useEngagementTypeDefaultActions";
import { useGoToEngagementTypeDetails } from "../../paths";

export type EngagementListSortField = "name" | "updatedAt";
export type EngagementListSortDirection = "asc" | "desc";
export type EngagementListSort = {
  field: EngagementListSortField;
  direction: EngagementListSortDirection;
};

export interface EngagementRowsTableProps extends WSMarginProps {
  engagements: Engagement[];
  loading?: boolean;
  isArchivedOnly?: boolean;
  filters?: any;
  sort?: EngagementListSort;
  onSort?: (sort: EngagementListSort) => void;
}

export const EngagementRowsTable: React.FC<EngagementRowsTableProps> = ({
  engagements,
  loading,
  isArchivedOnly,
  filters,
  sort,
  onSort,
  ...elementProps
}) => {
  const history = useHistory();
  const requirementsDefinitionsListQuery =
    queriesRequirementDefinitions.listRequirementDefinitions.useQueryAll();

  const directionFor = (
    field: EngagementListSortField
  ): WSTableColumnSortDirection => {
    if (!sort || sort.field !== field) return "none";
    return sort.direction === "asc" ? "ascending" : "descending";
  };

  const handleColumnSort =
    (field: EngagementListSortField) =>
    (direction: WSTableColumnSortDirection) => {
      if (!onSort) return;
      // Library cycles asc → none → desc; we don't support "unsorted",
      // so treat "none" as a flip to descending on the same column.
      if (direction === "none") {
        onSort({ field, direction: "desc" });
        return;
      }
      onSort({
        field,
        direction: direction === "ascending" ? "asc" : "desc"
      });
    };

  const goToEngagementTypeDetails = useGoToEngagementTypeDetails();

  const engagementTypeDefaultActions = useEngagementTypeDefaultActions();
  const requirementDefinitions = requirementsDefinitionsListQuery.flatData;

  const isComponentLoading = requirementsDefinitionsListQuery.isInitialLoading;
  const tableData = engagements.map(e => {
    return {
      data: e,
      id: e.id ?? ""
    };
  });

  return (
    <WSTable<Engagement>
      {...elementProps}
      loading={loading || isComponentLoading}
      tableData={tableData}
      onRowClick={({ data }) => {
        if (data.id) {
          goToEngagementTypeDetails(data.id);
        }
      }}
      rowFetchMenuActions={({ data }) => {
        return engagementTypeDefaultActions({ engagement: data });
      }}
      // headerAction={{
      //   icon: "download",
      //   onClick: async () => {
      //     // TODO: Implement download csv
      //   },
      //   loading: false
      // }}
      columns={[
        {
          config: {
            gridTemplateSizeMin: "0",
            gridTemplateSizeMax: "1.5fr",
            header: {
              text: "Name"
            },
            sortDirection: directionFor("name"),
            onColumnSort: handleColumnSort("name")
          },
          renderFunction: ({ data }) => {
            return (
              <WSTableCell text={data.name} secondaryText={data.description} />
            );
          }
        },
        // {
        //   config: {
        //     gridTemplateSizeMin: "0",
        //     gridTemplateSizeMax: "0.5fr",
        //     header: {
        //       text: "Type"
        //     },
        //     hideOnScreens: ["XS"]
        //   },
        //   renderFunction: ({ data }) => {
        //     return (
        //       <WSTableCell
        //         text={getEngagementLabelByType(data.type)}
        //       />
        //     );
        //   }
        // },
        {
          config: {
            gridTemplateSizeMin: "0",
            gridTemplateSizeMax: "0.5fr",
            header: {
              text: "Requirements"
            },
            hideOnScreens: ["XS"]
          },
          renderFunction: ({ data }) => {
            const requirementIds = data.requirementIds ?? [];
            const requirements = requirementIds.map(id =>
              requirementDefinitions?.find(r => r.id === id)
            );

            return requirements?.length > 0 ? (
              <WSTableCell
                text={requirements[0]?.name}
                secondaryText={
                  requirementIds.length > 1
                    ? `+${requirements.length - 1} more`
                    : undefined
                }
              />
            ) : (
              <WSTableCell text="--" />
            );
          }
        },
        {
          config: {
            gridTemplateSizeMin: "0",
            gridTemplateSizeMax: "0.5fr",
            header: {
              text: "Contractors"
            },
            hideOnScreens: ["XS"]
          },
          renderFunction: ({ id }) => {
            return <ContractorsCountCell engagementId={id} />;
          }
        },
        {
          config: {
            header: {
              text: "Last updated"
            },
            hideOnScreens: ["XS"],
            sortDirection: directionFor("updatedAt"),
            onColumnSort: handleColumnSort("updatedAt")
          },
          renderFunction: ({ data }) => {
            return (
              <WSTableCell
                text={toWSDateString(data.events?.updatedAt, "monthDayYear")}
              />
            );
          }
        },
        {
          config: {
            gridTemplateSizeMin: "0",
            gridTemplateSizeMax: "0.5fr",
            header: {
              text: "Status"
            }
          },
          renderFunction: ({ data }) => {
            if (data.status === EngagementStatus.ACTIVE) {
              return (
                <WSTableCell
                  pill={{
                    icon: true,
                    theme: "success",
                    text: "Active"
                  }}
                />
              );
            } else if (data.status === EngagementStatus.ARCHIVED) {
              return (
                <WSTableCell
                  pill={{
                    icon: "archive",
                    theme: "neutral",
                    text: "Archived"
                  }}
                />
              );
            }
          }
        }
      ]}
    />
  );
};
